import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CalendarDays, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DayCountCalculatorPage/components/CalculationForm';
import ResultsDisplay from './DayCountCalculatorPage/components/ResultsDisplay';
import CalendarVisualization from './DayCountCalculatorPage/components/CalendarVisualization';
import InfoContent from './DayCountCalculatorPage/components/InfoContent';
import HolidaysSidebar from './DayCountCalculatorPage/components/HolidaysSidebar';
import QuickToolsSidebar from './DayCountCalculatorPage/components/QuickToolsSidebar';
import SavedCalculationsSidebar from './DayCountCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './DayCountCalculatorPage/components/TipsSidebar';

// Импорт утилитарных функций
import { calculateDateDifference, getDatesBetween, getWorkingDaysBetween } from '../../../utils/calculations/date';
import { formatDate } from '../../../utils/formatting';

// Определение начального состояния формы
export const initialFormData = {
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString().split('T')[0],
  calculationType: 'difference', // 'difference', 'add', 'subtract'
  daysToAddSubtract: '7',
  includeEndDate: true,
  countWorkingDaysOnly: false,
  countSpecificDay: false,
  specificDay: '1', // 0 - воскресенье, 1 - понедельник, и т.д.
  excludeHolidays: false,
  customHolidays: [],
  useRussianHolidays: true,
};

// Основные российские праздники (фиксированные)
export const russianHolidays = [
  { date: '2025-01-01', name: 'Новый год' },
  { date: '2025-01-02', name: 'Новогодние каникулы' },
  { date: '2025-01-03', name: 'Новогодние каникулы' },
  { date: '2025-01-04', name: 'Новогодние каникулы' },
  { date: '2025-01-05', name: 'Новогодние каникулы' },
  { date: '2025-01-06', name: 'Новогодние каникулы' },
  { date: '2025-01-07', name: 'Рождество Христово' },
  { date: '2025-01-08', name: 'Новогодние каникулы' },
  { date: '2025-02-23', name: 'День защитника Отечества' },
  { date: '2025-03-08', name: 'Международный женский день' },
  { date: '2025-05-01', name: 'Праздник Весны и Труда' },
  { date: '2025-05-09', name: 'День Победы' },
  { date: '2025-06-12', name: 'День России' },
  { date: '2025-11-04', name: 'День народного единства' }
];

// Типы периодов для быстрого выбора
export const periodTypes = [
  { id: 'day', name: 'День' },
  { id: 'week', name: 'Неделя' },
  { id: 'month', name: 'Месяц' },
  { id: 'quarter', name: 'Квартал' },
  { id: 'year', name: 'Год' }
];

/**
 * DayCountCalculatorPage Component - Полный калькулятор расчета дней между датами,
 * добавления/вычитания дней и расчета рабочих дней.
 */
const DayCountCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('date-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Календарный счётчик дней | Gensio';
  const metaDescription = tool?.metaDescription || 'Точный расчет количества дней между датами, добавление или вычитание дней, недель, месяцев, расчет рабочих дней с учетом выходных и праздников России.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [dateRange, setDateRange] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedDateCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных расчетов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'date-difference':
          newState.calculationType = 'difference';
          break;
        case 'add-subtract':
          newState.calculationType = 'add';
          break;
        case 'working-days':
          newState.calculationType = 'difference';
          newState.countWorkingDaysOnly = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setDateRange([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || generateCalculationName(),
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedDateCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Создание имени для расчета
  const generateCalculationName = () => {
    switch (formData.calculationType) {
      case 'difference':
        return `Период ${formatDate(formData.startDate, 'short')} - ${formatDate(formData.endDate, 'short')}`;
      case 'add':
        return `${formData.startDate} + ${formData.daysToAddSubtract} дн.`;
      case 'subtract':
        return `${formData.startDate} - ${formData.daysToAddSubtract} дн.`;
      default:
        return `Расчет дат ${new Date().toLocaleDateString('ru-RU')}`;
    }
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedDateCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      
      // Восстанавливаем диапазон дат для визуализации
      if (calcToLoad.formData.calculationType === 'difference') {
        const start = new Date(calcToLoad.formData.startDate);
        const end = new Date(calcToLoad.formData.endDate);
        setDateRange(getDatesBetween(start, end));
      }
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <CalendarDays className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Календарный счётчик дней'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              dateRange={dateRange}
              setDateRange={setDateRange}
              russianHolidays={russianHolidays}
              periodTypes={periodTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsDisplay results={results} />
                <CalendarVisualization 
                  dateRange={dateRange} 
                  startDate={formData.startDate}
                  endDate={formData.endDate}
                  holidays={formData.useRussianHolidays ? russianHolidays : []}
                  customHolidays={formData.customHolidays}
                  workingDaysOnly={formData.countWorkingDaysOnly}
                />
              </>
            )}

            {/* --- Информационный контент о датах и расчетах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрых расчетов --- */}
            <QuickToolsSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель праздников России --- */}
            <HolidaysSidebar 
              russianHolidays={russianHolidays}
              formData={formData}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Календарь */
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
          gap: 1px;
        }
        .calendar-day {
          aspect-ratio: 1 / 1;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .calendar-day.weekend {
          @apply bg-red-50 dark:bg-red-900/20;
        }
        .calendar-day.holiday {
          @apply bg-orange-50 dark:bg-orange-900/20;
        }
        .calendar-day.selected {
          @apply bg-cyan-50 dark:bg-cyan-900/20;
        }
        .calendar-day.start {
          @apply bg-cyan-100 dark:bg-cyan-800/40 font-semibold;
        }
        .calendar-day.end {
          @apply bg-cyan-100 dark:bg-cyan-800/40 font-semibold;
        }
        .calendar-day.working-day {
          @apply bg-emerald-50 dark:bg-emerald-900/20;
        }
      `}</style>
    </div>
  );
};

export default DayCountCalculatorPage;